import React, { useCallback, useState } from 'react';

import type { SourceContentType } from '../../types';

import { MalleableContentEphemeralView } from './ephemeral-preview';
import { RemixOverlay as RemixOptionsOverlay } from './remix-overlay';

type RemixViewState = 'options' | 'preview';

const COMPONENT_URL = 'http://example.com';

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** The text content currently selected by the user, shown in the remix options overlay. */
	selectedText: string;
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, visual aid, database). */
	onOptionSelect?: (type: string) => void;
	/** Optional callback when the user inserts the generated component URL into the page/surface. */
	onInsertIntoPage?: (url: string) => void;
	/** Destination for the ephemeral preview insert button (database, whiteboard, live, page). */
	sourceContentType?: SourceContentType;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Step 1 – the user picks a remix type via RemixOptionsOverlay.
 * Step 2 – an ephemeral preview opens, letting the user copy or insert the
 * component URL.
 */
export function RemixContainer({
	selectedText,
	onClose,
	onOptionSelect,
	onInsertIntoPage,
	sourceContentType,
}: RemixContainerProps): React.ReactElement {
	const [viewState, setViewState] = useState<RemixViewState>('options');

	const handleOptionSelect = useCallback(
		(type: string) => {
			onOptionSelect?.(type);
			setViewState('preview');
		},
		[onOptionSelect],
	);

	const handleInsert = useCallback(
		(url: string) => {
			onInsertIntoPage?.(url);
			onClose();
		},
		[onInsertIntoPage, onClose],
	);

	if (viewState === 'preview') {
		return (
			<MalleableContentEphemeralView
				iframeUrl={COMPONENT_URL}
				onInsertIntoPage={onInsertIntoPage ? handleInsert : undefined}
				onClose={onClose}
				sourceContentType={sourceContentType}
			/>
		);
	}

	return (
		<RemixOptionsOverlay
			selectedText={selectedText}
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
		/>
	);
}
